<script setup lang="ts">
import type { ContentPackage } from '~/models/Content/ContentPackage'
import type { ImageRelationField } from '~/models/Content/BaseField'
import { useI18n } from 'vue-i18n'
import { computed, toRef } from 'vue'
import { storeToRefs } from 'pinia'
import { faChevronRight } from '@fortawesome/duotone-regular-svg-icons'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useTeacherGuide from '~/composables/useTeacherGuide'
import useProductHelper from '~/composables/useProductHelper'
import useImage from '~/composables/useImage'
import useDescriptor from '~/composables/useDescriptor'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import Button from '~/components/ui/Button.vue'

const { content, showBreadcrumbs = true } = defineProps<{
  content: ContentPackage
  showBreadcrumbs?: boolean
}>()

const { t } = useI18n()
const { getDescriptor } = useDescriptor()
const { getImageSrc, getImageAltText } = useImage()
const { getProductFromResource } = useProductStore()
const { addonProductColorMap } = useProductHelper()
const { resolveBaseUrl, buildParentPackageRoute, buildTeacherArticleUrl } = useUrlBuilder()
const { isTeacher } = storeToRefs(useAuthStore())

const label = computed(() => getDescriptor(content))
const product = computed(() => getProductFromResource(content))
const showParentLink = computed(() => content.parentLocationContentTypeIdentifier
  && [ContentType.ProductHeader, ContentType.ProductPackage].includes(content.parentLocationContentTypeIdentifier))
const color = computed(() => product.value ? addonProductColorMap[product.value.ean] : 'green')

const breadcrumbs = computed(() => {
  const list = []

  if (product.value) {
    list.push({ title: product.value.aunivers.name, to: { path: resolveBaseUrl(product.value) } })
  }
  if (showParentLink.value) {
    list.push({
      title: content.parentLocationName,
      to: { path: buildParentPackageRoute(content.parentLocationId, content.parentLocationName, product.value) }
    })
  }
  list.push({ title: content.title, to: { path: '' } })

  return list
})

const image = computed(() => {
  const imageField = content.image as ImageRelationField

  return Number(content.image?.destinationContentId) > 0
    ? { src: getImageSrc(imageField, 'banner_large'), altText: getImageAltText(imageField) }
    : { src: content.thumbnail, altText: content.thumbnailAlt }
})

const { teacherArticle } = useTeacherGuide({
  contentId: computed(() => Number(content.teacherGuide?.destinationContentId ?? 0)),
  enabled: toRef(() => Number(content.teacherGuide?.destinationContentId) > 0),
  withContent: false,
})
</script>

<template>
  <section
    class="mx-auto mb-8 h-full py-8"
    :style="{ backgroundColor: `var(--color-${color}-5)` }"
  >
    <div
      v-if="showBreadcrumbs"
      class="max-w-screen-au mx-auto px-4 pb-8 sm:px-8"
    >
      <nav :aria-label="t('breadcrumbs')">
        <ol class="flex items-center gap-2 text-lg">
          <li
            v-for="({ title, to }, index) in breadcrumbs"
            :key="index"
          >
            <router-link
              :to="to"
              :aria-current="index === breadcrumbs.length - 1 && 'page'"
              class="hover:text-seagreen-40 focus-visible:ring"
              :class="index === breadcrumbs.length - 1 ? 'font-medium pointer-events-none' : 'underline underline-offset-2'"
            >
              {{ title }}
            </router-link>
            <font-awesome-icon
              v-if="index < breadcrumbs.length - 1"
              class="ml-2"
              :icon="faChevronRight"
            />
          </li>
        </ol>
      </nav>
    </div>
    <div class="max-w-screen-au mx-auto grid grid-cols-1 items-center gap-8 px-4 sm:px-8 md:grid-cols-2">
      <div
        v-if="image?.src"
        class="aspect-[3/2] rounded"
      >
        <img
          :src="image.src"
          class="size-full rounded object-cover"
          :alt="image.altText"
        >
      </div>
      <article class="animate-fade">
        <span
          class="text-orange-5 order-first self-start rounded-md px-2 py-1 text-xs font-semibold"
          :style="{ backgroundColor: `var(--color-${color}-50)` }"
          v-text="label"
        />
        <h1
          class="text-purple-60 my-4 text-2xl font-bold"
          v-text="content?.title"
        />
        <div class="au-cms prose-md">
          <RichTextRenderer :text="content?.intro" />
        </div>
        <Button
          v-if="isTeacher && teacherArticle"
          :to="buildTeacherArticleUrl(teacherArticle)"
          color="purple"
          size="lg"
        >
          {{ t('filters.forTeacher') }}
        </Button>
      </article>
    </div>
  </section>
</template>
